/* eslint-disable no-restricted-properties */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Route } from 'react-router-dom';
import useTitle from '../shared/hooks/usePageConfig/useTitle';
import app from '../app/app';
import { EmptyView, PageProgress, SwitchWith404 } from '../shared/components';
import withErrorGate from '../shared/components/withErrorGate';

const modulesCache = new Map<string, any>();

const transformRoutes = (routes: any[]) => {
  return routes.map((item: any) => {
    return {
      ...item,
      component: withErrorGate(item.component),
    };
  });
};

interface Props {
  entry: {
    styles: string[];
    scripts: string[];
  };
  name: string;
  __remote?: boolean;
  buildTime: string;
}

export default (props: Props) => {
  // 这个放后面，不然可能出问题
  useTitle('加载中...');

  const unmountedRef = useRef(false);
  const [thisModule, setThisModule] = useState<
    | {
        routes: any[];
        models?: any;
        mount?: () => void;
        unmount?: () => void;
      }
    | undefined
  >(modulesCache.get(props.name));
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const { entry } = props;

  const sortAssets = (a: string, b: string) => {
    if (a.match(/main[^\\]*\.js/)) {
      return 1;
    }

    return -1;
  };

  const getCssId = useCallback(
    (href: string) => {
      return `css-${props.name}-${href.split('/').pop()?.split('.')[0]}`;
    },
    [props.name]
  );

  const loadEntry = useCallback(() => {
    const alljs = Array.from(entry.scripts as string[]).sort(sortAssets);
    const allcss = Array.from((entry.styles || []) as string[]).sort(
      sortAssets
    );
    const jsEntry = alljs.pop() as string;

    setError('');
    setLoading(true);

    Promise.all([
      ...allcss.map(item => {
        return new Promise((resolve, reject) => {
          const id = getCssId(item);

          if (document.getElementById(id)) {
            resolve('');

            return;
          }

          const styleEl = document.createElement('link');

          styleEl.rel = 'stylesheet';
          styleEl.type = 'text/css';
          styleEl.id = id;
          styleEl.href = item;
          styleEl.onload = () => {
            resolve('');
          };
          styleEl.onerror = () => {
            document.head.removeChild(styleEl);
            reject(new Error(`应用【${item}】资源 ${item} 加载失败`));
          };

          document.head.appendChild(styleEl);
        });
      }),
      ...alljs.map(js => System.import(js)),
    ])
      .then(() => System.import(jsEntry))
      .then(sourceModule => {
        // @ts-ignore
        sourceModule = sourceModule.routes
          ? sourceModule
          : window[('$$' + props.name) as any];
        const { routes, models, mount, unmount } = sourceModule;

        const modu = {
          routes,
          models,
          mount,
          unmount,
        };

        modu.routes = transformRoutes(modu.routes);
        modulesCache.set(props.name, modu);
        setThisModule(modu);

        for (const key in modu.models) {
          app.model(modu.models[key]);
        }

        if (unmountedRef.current) return;

        modu.mount && modu.mount();
      })
      .catch(e => {
        console.log('子应用加载失败：', e);

        setError('加载失败，请点击重试');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [entry.scripts, entry.styles, getCssId, props.name]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && props.__remote) {
      console.log(
        `%c【提示】 应用 ${props.name} 未在开发环境下启动，降级使用远端资源`,
        'color:#fa8c16;'
      );
    }

    const modu = modulesCache.get(props.name);

    // 没有缓存时去获取，有则已在初始化时获取
    if (!modu) {
      loadEntry();
    } else {
      modu.mount && modu.mount();
    }

    return () => {
      unmountedRef.current = true;
      thisModule && thisModule.unmount && thisModule.unmount();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <PageProgress />;
  }

  if (error) {
    return (
      <EmptyView
        text={
          <div
            onClick={() => {
              // TODO: 不要页面刷新
              window.location.reload();
            }}
          >
            {error}
          </div>
        }
      />
    );
  }

  const routes = thisModule ? thisModule.routes : [];

  return (
    <div id="micro-insideapp-container" ref={containerRef}>
      {routes && routes.length ? (
        <SwitchWith404>
          {routes.map(item => {
            return <Route key={item.path} {...item} />;
          })}
        </SwitchWith404>
      ) : null}
    </div>
  );
};
